/**
 * Responsibility: Implement notebook model behavior for the ui/notebook surface.
 * Scope: Owns ui/notebook concerns only; cross-surface orchestration is excluded.
 */
import type { NotebookSection } from "@anyquest/shared-core"

const TOPIC_LABELS: Record<string, string> = {
  world: 'WORLD',
  location: 'LOCATIONS',
  people: 'PEOPLE',
  event: 'EVENTS',
  quest: 'QUESTS',
  atlas: 'ATLAS',
}

const TOPIC_ORDER = ['world', 'location', 'people', 'event', 'quest', 'atlas']

type NotebookEntry = {
  topic: string
  text: string
}

export class NotebookModel {
  static empty() {
    return new NotebookModel(0, [])
  }

  static fromNotes(rawNotes: unknown[], limit = 120) {
    const notes = Array.isArray(rawNotes) ? rawNotes.slice(0, limit) : []
    if (!notes.length) return NotebookModel.empty()

    const groups = new Map(TOPIC_ORDER.map(topic => [topic, [] as string[]]))
    for (const raw of notes) {
      const parsed = parseNotebookEntry(raw)
      if (!parsed) continue
      if (!groups.has(parsed.topic)) groups.set(parsed.topic, [])
      groups.get(parsed.topic)?.push(parsed.text)
    }

    const sections: NotebookSection[] = []
    for (const topic of TOPIC_ORDER) {
      const entries = groups.get(topic) ?? []
      if (!entries.length) continue
      const visible = entries.slice(0, 20)
      sections.push({
        key: topic,
        label: TOPIC_LABELS[topic] || topic.toUpperCase(),
        entries: visible,
        overflow: Math.max(0, entries.length - visible.length),
      })
    }

    return new NotebookModel(notes.length, sections)
  }

  totalEntries: number
  sections: NotebookSection[]

  constructor(totalEntries: number, sections: NotebookSection[]) {
    this.totalEntries = Math.max(0, Number(totalEntries || 0))
    this.sections = normalizeNotebookSections(sections)
  }

  get hasEntries() {
    return this.totalEntries > 0
  }

  get hasSections() {
    return this.sections.length > 0
  }

  get meta() {
    return `${this.totalEntries} ENTRIES`
  }

  get emptyDescription() {
    return this.hasEntries
      ? 'No categorized notes available yet.'
      : 'No entries yet. Discover places, people, and outcomes to build your notebook.'
  }

  initialOpenState() {
    const open: Record<string, boolean> = {}
    for (const section of this.sections.slice(0, 2)) {
      if (!section?.key) continue
      open[String(section.key).trim().toLowerCase()] = true
    }
    return open
  }

  toDialogPayload() {
    return {
      content: 'notebook-dialog',
      data: {
        model: this,
        desc: this.hasSections ? null : this.emptyDescription,
        meta: this.meta,
      },
      options: {
        title: 'NOTEBOOK',
        variant: 'notebook' as const,
        swipeDismiss: true,
      },
    }
  }
}

export function normalizeNotebookSections(rawSections: unknown[]): NotebookSection[] {
  if (!Array.isArray(rawSections)) return []
  const sections: NotebookSection[] = []
  for (const raw of rawSections) {
    const key = String((raw as NotebookSection)?.key ?? '').trim().toLowerCase()
    const label = String((raw as NotebookSection)?.label ?? key).trim()
    const entries = Array.isArray((raw as NotebookSection)?.entries)
      ? (raw as NotebookSection).entries.map(entry => String(entry ?? '').trim()).filter(Boolean).slice(0, 20)
      : []
    if (!key || !label || !entries.length) continue
    sections.push({
      key,
      label,
      entries,
      overflow: Math.max(0, Number((raw as NotebookSection)?.overflow ?? 0) || 0),
    })
  }
  return sections
}

export function parseNotebookEntry(raw: unknown): NotebookEntry | null {
  const text = String(raw ?? '').trim()
  if (!text) return null
  const tagged = text.match(/^\[([A-Z]+)\]\s*(.+)$/)
  if (!tagged) return { topic: 'event', text }
  const topic = String(tagged[1] ?? '').toLowerCase()
  const body = String(tagged[2] ?? '').trim()
  if (!body) return null
  return { topic, text: body }
}
